/**
 * Storage Service
 * Wraps storage-client.js with higher-level helper functions
 * Provides convenient methods for common storage operations
 */

import {
    loadData,
    saveData,
    loadManyData,
    loadFeedbackIndex,
    saveFeedbackIndex,
    loadFeedbackItem,
    saveFeedbackItem,
    // D1 typed helpers
    fetchStartupData,
    batchSaveTasks,
    batchSaveProjects,
    saveUserData,
    saveDependencies,
    saveHistory,
    saveTemplatesD1,
    // Row-level D1 helpers (Phase 2)
    insertTaskD1,
    updateTaskD1,
    deleteTaskD1,
    reorderTasksD1,
    insertProjectD1,
    updateProjectD1,
    deleteProjectD1,
    reserveIdsD1,
} from "../../storage-client.js?v=20261005-id-reservation";

/**
 * Save all main application data (tasks, projects, feedback)
 * @param {Array} tasks - Tasks array
 * @param {Array} projects - Projects array
 * @param {Array} feedbackItems - Feedback items array
 * @returns {Promise<void>}
 */
export async function saveAll(tasks, projects, feedbackItems) {
    const previousTasksCache    = loadArrayCache(TASKS_CACHE_KEY);
    const previousProjectsCache = loadArrayCache(PROJECTS_CACHE_KEY);
    if (Array.isArray(tasks))    persistArrayCache(TASKS_CACHE_KEY,    tasks);
    if (Array.isArray(projects)) persistArrayCache(PROJECTS_CACHE_KEY, projects);

    try {
        await Promise.all([
            batchSaveTasks(tasks),
            batchSaveProjects(projects),
            saveFeedbackItems(feedbackItems)
        ]);
    } catch (error) {
        // Rollback cache on network failure
        persistArrayCache(TASKS_CACHE_KEY,    previousTasksCache);
        persistArrayCache(PROJECTS_CACHE_KEY, previousProjectsCache);
        console.error("Error saving all data:", error);
        throw error;
    }
}

/**
 * Save tasks to storage (cache-first for optimistic UI)
 * @param {Array} tasks - Tasks array
 * @returns {Promise<void>}
 */
export async function saveTasks(tasks) {
    // Cache-first: update localStorage immediately for instant refresh persistence
    const previousCache = loadArrayCache(TASKS_CACHE_KEY);
    if (Array.isArray(tasks)) persistArrayCache(TASKS_CACHE_KEY, tasks);

    try {
        await batchSaveTasks(tasks);
    } catch (error) {
        // Rollback cache on network failure
        persistArrayCache(TASKS_CACHE_KEY, previousCache);
        const msg = error?.message ?? String(error);
        const stack = error?.stack;
        console.error("[Storage Service] saveTasks failed:", msg);
        if (stack) console.error("[Storage Service] Stack:", stack);
        throw error;
    }
}

/**
 * Save projects to storage (cache-first for optimistic UI)
 * @param {Array} projects - Projects array
 * @returns {Promise<void>}
 */
export async function saveProjects(projects) {
    // Cache-first: update localStorage immediately for instant refresh persistence
    const previousCache = loadArrayCache(PROJECTS_CACHE_KEY);
    if (Array.isArray(projects)) persistArrayCache(PROJECTS_CACHE_KEY, projects);

    try {
        await batchSaveProjects(projects);
    } catch (error) {
        // Rollback cache on network failure
        persistArrayCache(PROJECTS_CACHE_KEY, previousCache);
        console.error("Error saving projects:", error);
        throw error;
    }
}

/**
 * Save feedback items to storage (cache-first for optimistic UI)
 * @param {Array} feedbackItems - Feedback items array
 * @returns {Promise<void>}
 */
export async function saveFeedbackItems(feedbackItems) {
    const items = Array.isArray(feedbackItems) ? feedbackItems : [];

    // Cache-first: update localStorage immediately for instant refresh persistence
    const previousCache = loadFeedbackCache(FEEDBACK_CACHE_KEY);
    persistFeedbackCache(FEEDBACK_CACHE_KEY, items);

    try {
        // Use D1 API for each item
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
            throw new Error('Not authenticated');
        }

        // Save each item to D1
        await Promise.all(items.map(async (item) => {
            const response = await fetch('/api/feedback-d1', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(item)
            });
            
            if (!response.ok) {
                throw new Error(`Failed to save item ${item.id}: ${response.status}`);
            }
        }));
    } catch (error) {
        // Rollback cache on network failure
        persistFeedbackCache(FEEDBACK_CACHE_KEY, previousCache);
        console.error("Error saving feedback items:", error);
        throw error;
    }
}

/**
 * Save project colors map to storage
 * @param {Object} projectColorMap - Project colors map
 * @returns {Promise<void>}
 */
export async function saveProjectColors(projectColorMap) {
    try {
        await saveUserData({ projectColors: projectColorMap });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving project colors:", error);
        throw error;
    }
}

/**
 * Save project color overrides (user-explicitly-chosen colors, never wiped by migrations)
 * @param {Object} projectColorOverrides - Map of projectId -> hex color
 * @returns {Promise<void>}
 */
export async function saveProjectColorOverrides(projectColorOverrides) {
    try {
        await saveUserData({ projectColorOverrides });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving project color overrides:", error);
        throw error;
    }
}

/**
 * Save tag color overrides (user-explicitly-chosen colors, never wiped by migrations)
 * @param {Object} tagColorOverrides - Map of tag key -> hex color
 * @returns {Promise<void>}
 */
export async function saveTagColorOverrides(tagColorOverrides) {
    try {
        await saveUserData({ tagColorOverrides });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving tag color overrides:", error);
        throw error;
    }
}

/**
 * Save tag colors map to storage
 * @param {Object} tagColorMap - Tag colors map
 * @returns {Promise<void>}
 */
export async function saveTagColors(tagColorMap) {
    try {
        await saveUserData({ tagColors: tagColorMap });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving tag colors:", error);
        throw error;
    }
}

/**
 * Save tag color assignment version to storage
 * @param {number} version - Tag color assignment version
 * @returns {Promise<void>}
 */
export async function saveTagColorsVersion(version) {
    try {
        await saveUserData({ tagColorsVersion: version });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving tag colors version:", error);
        throw error;
    }
}

/**
 * Save sort mode and manual task order
 * @param {string} sortMode - Sort mode ('priority' or 'manual')
 * @param {Object} manualTaskOrder - Manual task order map
 * @returns {Promise<void>}
 */
export async function saveSortState(sortMode, manualTaskOrder) {
    try {
        await saveUserData({ sortMode, manualTaskOrder });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving sort state:", error);
        throw error;
    }
}

/**
 * Save project templates to storage
 * @param {Array} templates - Templates array
 * @returns {Promise<void>}
 */
export async function saveTemplates(templates) {
    try {
        await saveTemplatesD1(templates);
    } catch (error) {
        console.error("Error saving templates:", error);
        throw error;
    }
}

/**
 * Load project templates from storage
 * @returns {Promise<Array>}
 */
export async function loadTemplates() {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) return [];
        const response = await fetch('/api/user-data?resource=templates', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data.templates) ? data.templates : [];
    } catch (error) {
        console.error("Error loading templates:", error);
        return [];
    }
}

/**
 * Load all main application data
 * @returns {Promise<{tasks: Array, projects: Array, feedbackItems: Array}>}
 */
export async function loadAll(options = {}) {
    const preferCache = !!options.preferCache;
    const feedbackOptions = options.feedback || {};

    try {
        const cachedTasks = preferCache ? loadArrayCache(TASKS_CACHE_KEY) : [];
        const cachedProjects = preferCache ? loadArrayCache(PROJECTS_CACHE_KEY) : [];

        const hasCached = (cachedTasks && cachedTasks.length > 0) || (cachedProjects && cachedProjects.length > 0);
        if (preferCache && hasCached) {
            // Fast path: tasks/projects are in localStorage — return immediately.
            // Feedback is a synchronous cache read (no network). If the feedback
            // cache is empty the app renders with [] and the background refresh
            // below delivers feedback via onRefresh once it arrives. This prevents
            // a cold FEEDBACK_DB round-trip (~20 s) from blocking the entire init.
            const feedbackCacheKey = feedbackOptions.cacheKey || FEEDBACK_CACHE_KEY;
            const cachedFeedback = loadFeedbackCache(feedbackCacheKey);

            const onRefresh = typeof options.onRefresh === "function" ? options.onRefresh : null;
            const refreshOptions = {
                ...options,
                preferCache: false,
                feedback: { ...feedbackOptions, preferCache: false }
            };
            void loadAllNetwork(refreshOptions).then((fresh) => {
                if (onRefresh) onRefresh(fresh);
            });
            return {
                tasks:           cachedTasks    || [],
                projects:        cachedProjects || [],
                feedbackItems:   cachedFeedback || [],
                // D1 fields not available from cache — caller gets them on network refresh via onRefresh
                settings:        {},
                projectColors:   {},
                tagColors:       {},
                tagColorsVersion: 0,
                sortMode:        'priority',
                manualTaskOrder: {},
                templates:       [],
                dependencies:    {},
            };
        }

        // Cold path: no cached tasks/projects — must wait for full network fetch.
        const feedbackPreferCache = feedbackOptions.preferCache ?? preferCache;
        return await loadAllNetwork({
            ...options,
            feedback: { ...feedbackOptions, preferCache: feedbackPreferCache }
        });
    } catch (error) {
        console.error("Error loading all data:", error);
        return {
            tasks:           [],
            projects:        [],
            feedbackItems:   [],
            settings:        {},
            projectColors:   {},
            tagColors:       {},
            tagColorsVersion: 0,
            sortMode:        'priority',
            manualTaskOrder: {},
            templates:       [],
            dependencies:    {},
        };
    }
}

const FEEDBACK_CACHE_KEY = "feedbackItemsCache:v1";
const TASKS_CACHE_KEY = "tasksCache:v1";
const PROJECTS_CACHE_KEY = "projectsCache:v1";
const CACHE_TOKEN_KEY = "nautilus_cache_token:v1";

function getScopedCacheKey(baseKey, tokenOverride) {
    try {
        const token = tokenOverride ?? localStorage.getItem("authToken");
        return token ? `${baseKey}:${token}` : baseKey;
    } catch (e) {
        return baseKey;
    }
}

function loadArrayCache(baseKey) {
    try {
        const token = localStorage.getItem("authToken");
        const scopedKey = getScopedCacheKey(baseKey, token);
        const raw = localStorage.getItem(scopedKey);
        const parsed = raw ? JSON.parse(raw) : [];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;

        // Do NOT migrate "previous" cache: that was another user's data. Returning it
        // on login-as-different-user caused 1–3s of wrong dashboard before network refresh.
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function persistArrayCache(baseKey, items) {
    try {
        const token = localStorage.getItem("authToken");
        localStorage.setItem(getScopedCacheKey(baseKey, token), JSON.stringify(items || []));
        if (token) {
            localStorage.setItem(CACHE_TOKEN_KEY, token);
        }
    } catch (e) {}
}

function patchArrayCacheById(baseKey, id, changes) {
    const previousCache = loadArrayCache(baseKey);
    if (!Array.isArray(previousCache) || previousCache.length === 0) return previousCache;

    const numericId = Number(id);
    let changed = false;
    const nextCache = previousCache.map((item) => {
        if (Number(item?.id) !== numericId) return item;
        changed = true;
        return { ...item, ...changes };
    });
    if (changed) persistArrayCache(baseKey, nextCache);
    return previousCache;
}

function loadFeedbackCache(cacheKey) {
    const key = cacheKey || FEEDBACK_CACHE_KEY;
    try {
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function persistFeedbackCache(cacheKey, items) {
    const key = cacheKey || FEEDBACK_CACHE_KEY;
    try {
        localStorage.setItem(key, JSON.stringify(items || []));
    } catch (e) {}
}

function applyFeedbackLimits(items, options) {
    const limitPending = options.limitPending;
    const limitDone = options.limitDone;
    if (!Array.isArray(items)) return items;
    if (!Number.isInteger(limitPending) && !Number.isInteger(limitDone)) return items;

    const pending = [];
    const done = [];
    for (const item of items) {
        if (!item) continue;
        const status = item.status === 'done' ? 'done' : 'open';
        if (status === 'done') done.push(item);
        else pending.push(item);
    }
    const limitedPending = Number.isInteger(limitPending) ? pending.slice(0, limitPending) : pending;
    const limitedDone = Number.isInteger(limitDone) ? done.slice(0, limitDone) : done;
    return [...limitedPending, ...limitedDone];
}

async function loadAllNetwork(options = {}) {
    const feedbackOptions = options.feedback || {};

    // Load tasks+projects+settings from D1, and feedback from D1 in parallel
    const [startupData, feedbackItems] = await Promise.all([
        fetchStartupData(),
        loadFeedbackItemsFromIndex(feedbackOptions),
    ]);

    const safeTasks    = Array.isArray(startupData?.tasks)    ? startupData.tasks    : [];
    const safeProjects = Array.isArray(startupData?.projects) ? startupData.projects : [];
    const safeFeedback = Array.isArray(feedbackItems)         ? feedbackItems        : [];

    persistArrayCache(TASKS_CACHE_KEY,    safeTasks);
    persistArrayCache(PROJECTS_CACHE_KEY, safeProjects);
    const cacheKey = feedbackOptions.cacheKey || FEEDBACK_CACHE_KEY;
    persistFeedbackCache(cacheKey, safeFeedback);

    return {
        tasks:        safeTasks,
        projects:     safeProjects,
        feedbackItems: safeFeedback,
        // Pass through all D1 startup fields for app.js to consume
        settings:        startupData?.settings        ?? {},
        projectColors:   startupData?.projectColors   ?? {},
        tagColors:       startupData?.tagColors        ?? {},
        tagColorsVersion: startupData?.tagColorsVersion ?? 0,
        sortMode:        startupData?.sortMode         ?? 'priority',
        manualTaskOrder: startupData?.manualTaskOrder  ?? {},
        templates:       startupData?.templates        ?? [],
        dependencies:    startupData?.dependencies     ?? {},
    };
}

function mergeFeedbackItems(baseItems, updates) {
    const base = Array.isArray(baseItems) ? baseItems : [];
    const incoming = Array.isArray(updates) ? updates : [];
    if (base.length === 0) return incoming.filter(Boolean);

    const baseIds = new Set();
    const mergedBase = [];
    const updateMap = new Map();

    for (const item of incoming) {
        if (item && item.id != null) {
            updateMap.set(item.id, item);
        }
    }

    for (const item of base) {
        if (!item || item.id == null) continue;
        baseIds.add(item.id);
        mergedBase.push(updateMap.get(item.id) || item);
    }

    const newItems = [];
    for (const item of incoming) {
        if (item && item.id != null && !baseIds.has(item.id)) {
            newItems.push(item);
        }
    }

    return [...newItems, ...mergedBase];
}

async function loadFeedbackItemsByStatus(index, limitPending, limitDone) {
    const pendingLimit = Number.isInteger(limitPending) ? limitPending : null;
    const doneLimit = Number.isInteger(limitDone) ? limitDone : null;

    // PERFORMANCE FIX: Load all items in PARALLEL first, then filter by status
    // Old approach: sequential for loop with await (SLOW!)
    // New approach: Promise.all + filter (FAST!)
    const allItems = await Promise.all(index.map((id) => loadFeedbackItem(id)));

    const pending = [];
    const done = [];

    // Separate items by status
    for (const item of allItems) {
        if (!item) continue;
        const status = item.status === 'done' ? 'done' : 'open';
        if (status === 'done') {
            done.push(item);
        } else {
            pending.push(item);
        }
    }

    // Apply limits
    const limitedPending = pendingLimit !== null ? pending.slice(0, pendingLimit) : pending;
    const limitedDone = doneLimit !== null ? done.slice(0, doneLimit) : done;

    return [...limitedPending, ...limitedDone];
}

async function refreshFeedbackItemsFromIndex(options) {
    const limitPending = options.limitPending;
    const limitDone = options.limitDone;
    const cacheKey = options.cacheKey || FEEDBACK_CACHE_KEY;
    const cached = Array.isArray(options.cached) ? options.cached : [];
    const onRefresh = typeof options.onRefresh === 'function' ? options.onRefresh : null;

    try {
        const index = await loadFeedbackIndex();
        if (Array.isArray(index) && index.length > 0) {
            let items = [];
            if (Number.isInteger(limitPending) || Number.isInteger(limitDone)) {
                items = await loadFeedbackItemsByStatus(index, limitPending, limitDone);
            } else {
                items = await Promise.all(index.map((id) => loadFeedbackItem(id)));
            }
            const merged = mergeFeedbackItems(cached, items.filter(Boolean));
            persistFeedbackCache(cacheKey, merged);
            if (onRefresh) onRefresh(merged);
        }
    } catch (error) {
        console.error("Error refreshing feedback items:", error);
    }
}

async function loadFeedbackItemsFromIndex(options = {}) {
    // Load from D1 database
    let cached = [];
    try {
        const cacheKey = options.cacheKey || FEEDBACK_CACHE_KEY;
        cached = options.useCache === false ? [] : loadFeedbackCache(cacheKey);
        const preferCache = !!(options.preferCache && cached.length > 0);
        
        if (preferCache) {
            // Return cached immediately for instant UI
            // Refresh in background — D1 is authoritative, update cache even if empty
            void loadFeedbackFromD1().then((items) => {
                if (Array.isArray(items)) {
                    persistFeedbackCache(cacheKey, items);
                    if (typeof options.onRefresh === "function") {
                        options.onRefresh({ feedbackItems: items });
                    }
                }
            }).catch(() => {});
            return cached;
        }

        // Load from D1
        const items = await loadFeedbackFromD1();
        if (Array.isArray(items)) {
            persistFeedbackCache(cacheKey, items);
            return items;
        }

        // Fallback to cache only on network error (items === [] is valid: D1 is authoritative)
        return cached || [];
    } catch (error) {
        console.error("Error loading feedback items:", error);
        return cached || [];
    }
}

async function loadFeedbackFromD1() {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
            return [];
        }

        // Load all items from D1 (paginated, but get all for now)
        const response = await fetch('/api/feedback-d1?limit=1000', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Failed to load: ${response.status}`);
        }
        
        const data = await response.json();
        if (!data.success || !Array.isArray(data.items)) return [];
        // Normalize D1 snake_case columns to camelCase expected by the app
        return data.items.map(row => ({
            id: row.id,
            type: row.type,
            description: row.description,
            status: row.status,
            screenshotUrl: row.screenshot_url || row.screenshotUrl || '',
            createdAt: row.created_at || row.createdAt || '',
            createdBy: row.created_by || row.createdBy || '',
            resolvedAt: row.resolved_at || row.resolvedAt || null,
            attachmentFileKey: row.attachment_file_key || row.attachmentFileKey || '',
            attachmentFileName: row.attachment_file_name || row.attachmentFileName || '',
            attachmentFileType: row.attachment_file_type || row.attachmentFileType || ''
        }));
    } catch (error) {
        console.error("Error loading from D1:", error);
        return [];
    }
}

/**
 * Save a single feedback item to D1
 * @param {Object} item - Feedback item to save
 * @returns {Promise<void>}
 */
export async function saveSingleFeedbackItem(item) {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch('/api/feedback-d1', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(item)
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to save item ${item.id}: ${response.status} - ${errorText}`);
        }
    } catch (error) {
        console.error("Error saving single feedback item:", error);
        throw error;
    }
}

/**
 * Update a single feedback item in D1
 * @param {Object} item - Feedback item to update
 * @returns {Promise<void>}
 */
export async function updateSingleFeedbackItem(item) {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch('/api/feedback-d1', {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(item)
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to update item ${item.id}: ${response.status} - ${errorText}`);
        }
    } catch (error) {
        console.error("Error updating single feedback item:", error);
        throw error;
    }
}

/**
 * Delete a single feedback item from D1
 * @param {number} itemId - ID of feedback item to delete
 * @returns {Promise<void>}
 */
export async function deleteSingleFeedbackItem(itemId) {
    try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch('/api/feedback-d1', {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: itemId })
        });
        
        if (!response.ok) {
            // 404 = item doesn't exist in D1 (legacy KV item or already deleted) — treat as success
            if (response.status === 404) {
                return;
            }
            const errorText = await response.text();
            throw new Error(`Failed to delete item ${itemId}: ${response.status} - ${errorText}`);
        }
    } catch (error) {
        console.error("Error deleting single feedback item:", error);
        throw error;
    }
}

async function loadFeedbackFromIndex() {
    try {
        // Load index first (small, fast)
        const index = await loadData("feedback:index");
        if (!Array.isArray(index) || index.length === 0) {
            return [];
        }
        
        // Load all items in parallel (FAST!)
        const items = await Promise.all(
            index.map(id => loadData(`feedback:item:${id}`))
        );
        
        // Filter out nulls and return
        return items.filter(Boolean);
    } catch (error) {
        console.error("Error loading from index:", error);
        return [];
    }
}

/**
 * Load sort mode and manual task order
 * @returns {Promise<{sortMode: string, manualTaskOrder: Object}>}
 */
export async function loadSortState() {
    try {
        const data = await _fetchUserData();
        const sortMode = (!data.sortMode || data.sortMode === 'auto') ? 'priority' : data.sortMode;
        return {
            sortMode,
            manualTaskOrder: data.manualTaskOrder || { todo: [], progress: [], review: [], done: [] }
        };
    } catch (error) {
        console.error("Error loading sort state:", error);
        return {
            sortMode: 'priority',
            manualTaskOrder: { todo: [], progress: [], review: [], done: [] }
        };
    }
}

/**
 * Load project colors map
 * @returns {Promise<Object>}
 */
export async function loadProjectColors() {
    try {
        const data = await _fetchUserData();
        return data.projectColors || {};
    } catch (error) {
        console.error("Error loading project colors:", error);
        return {};
    }
}

/**
 * Load tag colors map
 * @returns {Promise<Object>}
 */
export async function loadTagColors() {
    try {
        const data = await _fetchUserData();
        return data.tagColors && typeof data.tagColors === "object" ? data.tagColors : {};
    } catch (error) {
        console.error("Error loading tag colors:", error);
        return {};
    }
}

/**
 * Load tag color assignment version
 * @returns {Promise<number>}
 */
export async function loadTagColorsVersion() {
    try {
        const data = await _fetchUserData();
        const parsed = Number(data.tagColorsVersion);
        return Number.isFinite(parsed) ? parsed : 0;
    } catch (error) {
        console.error("Error loading tag colors version:", error);
        return 0;
    }
}

/**
 * Load project color overrides (user-explicitly-chosen, never wiped by migrations)
 * @returns {Promise<Object>}
 */
export async function loadProjectColorOverrides() {
    try {
        const data = await _fetchUserData();
        return data.projectColorOverrides && typeof data.projectColorOverrides === "object"
            ? data.projectColorOverrides
            : {};
    } catch (error) {
        console.error("Error loading project color overrides:", error);
        return {};
    }
}

/**
 * Load tag color overrides (user-explicitly-chosen, never wiped by migrations)
 * @returns {Promise<Object>}
 */
export async function loadTagColorOverrides() {
    try {
        const data = await _fetchUserData();
        return data.tagColorOverrides && typeof data.tagColorOverrides === "object"
            ? data.tagColorOverrides
            : {};
    } catch (error) {
        console.error("Error loading tag color overrides:", error);
        return {};
    }
}

/**
 * Load automation rules for the current user from D1-backed user data.
 * Returns [] if none exist.
 * @returns {Promise<Array>}
 */
export async function loadAutomations() {
    try {
        const data = await _fetchUserData();
        return Array.isArray(data.automations) ? data.automations : [];
    } catch (error) {
        console.error("Error loading automations:", error);
        return [];
    }
}

/**
 * Save automation rules for the current user.
 * @param {Array} rules - Automation rule objects
 * @returns {Promise<void>}
 */
export async function saveAutomations(rules) {
    try {
        await saveUserData({ automations: Array.isArray(rules) ? rules : [] });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving automations:", error);
        throw error;
    }
}

/**
 * Load saved Kai prompts for the current user from D1-backed user data.
 * Returns [] if none exist.
 * @returns {Promise<Array>}
 */
export async function loadSavedPrompts() {
    try {
        const data = await _fetchUserData();
        return Array.isArray(data.savedPrompts) ? data.savedPrompts : [];
    } catch (error) {
        console.error("Error loading saved prompts:", error);
        return [];
    }
}

/**
 * Save the user's bookmarked Kai prompts.
 * @param {Array} prompts - Saved-prompt objects { id, text, createdAt }
 * @returns {Promise<void>}
 */
export async function saveSavedPrompts(prompts) {
    try {
        await saveUserData({ savedPrompts: Array.isArray(prompts) ? prompts : [] });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving saved prompts:", error);
        throw error;
    }
}

/**
 * Save application settings (per user) to storage
 * @param {Object} settings - Settings object
 * @returns {Promise<void>}
 */
export async function saveSettings(settings) {
    try {
        await saveUserData({ settings });
        invalidateUserDataCache();
    } catch (error) {
        console.error("Error saving settings:", error);
        throw error;
    }
}

/**
 * Load application settings (per user) from storage
 * @returns {Promise<Object>}
 */
export async function loadSettings() {
    try {
        const data = await _fetchUserData();
        return data.settings || {};
    } catch (error) {
        console.error("Error loading settings:", error);
        return {};
    }
}

/**
 * Shared helper: fetch user-data from D1 in one request.
 * Cached per micro-task tick to avoid duplicate calls when loadProjectColors +
 * loadTagColors + loadSortState are called in the same Promise.all.
 */
let _userDataCache = null;
let _userDataCacheExpiry = 0;
function invalidateUserDataCache() {
    _userDataCache = null;
    _userDataCacheExpiry = 0;
}
async function _fetchUserData() {
    const now = Date.now();
    if (_userDataCache && now < _userDataCacheExpiry) return _userDataCache;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) return {};
    const response = await fetch('/api/user-data', {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (!response.ok) return {};
    const data = await response.json();
    _userDataCache = data.success ? data : {};
    _userDataCacheExpiry = now + 5000; // 5s cache to avoid stampedes on startup
    return _userDataCache;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Row-level D1 Operations (Phase 2)
// These functions call D1 helpers directly without updating localStorage cache.
// Cache is intentionally stale between row ops; recovered on next page load.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Reserve a block of task or project ids for this device (server-allocated, never
 * shared with another device or the recurring job).
 * @param {'task'|'project'} kind
 * @param {number} count
 * @returns {Promise<{start: number, end: number}>}
 */
export async function reserveIds(kind, count) {
    return reserveIdsD1(kind, count);
}

/**
 * Insert a single task into D1
 * @param {Object} task - Task object with all fields
 * @returns {Promise<void>}
 */
export async function saveTaskCreate(task) {
    try {
        await insertTaskD1(task);
    } catch (error) {
        console.error("Error creating task:", error);
        throw error;
    }
}

/**
 * Update a single task in D1
 * @param {number} id - Task ID
 * @param {Object} changes - Object with only changed fields
 * @returns {Promise<void>}
 */
export async function saveTaskUpdate(id, changes) {
    const previousCache = patchArrayCacheById(TASKS_CACHE_KEY, id, changes);
    try {
        await updateTaskD1(id, changes);
    } catch (error) {
        persistArrayCache(TASKS_CACHE_KEY, previousCache);
        console.error("Error updating task:", error);
        throw error;
    }
}

/**
 * Delete a single task from D1
 * @param {number} id - Task ID
 * @returns {Promise<void>}
 */
export async function saveTaskDelete(id) {
    try {
        await deleteTaskD1(id);
    } catch (error) {
        console.error("Error deleting task:", error);
        throw error;
    }
}

/**
 * Reorder tasks by updating project_order for multiple tasks
 * @param {Array} updates - Array of { id, projectOrder } objects
 * @returns {Promise<void>}
 */
export async function saveTasksReorder(updates) {
    try {
        await reorderTasksD1(updates);
    } catch (error) {
        console.error("Error reordering tasks:", error);
        throw error;
    }
}

/**
 * Insert a single project into D1
 * @param {Object} project - Project object with all fields
 * @returns {Promise<void>}
 */
export async function saveProjectCreate(project) {
    try {
        await insertProjectD1(project);
    } catch (error) {
        console.error("Error creating project:", error);
        throw error;
    }
}

/**
 * Update a single project in D1
 * @param {number} id - Project ID
 * @param {Object} changes - Object with only changed fields
 * @returns {Promise<void>}
 */
export async function saveProjectUpdate(id, changes) {
    const previousCache = patchArrayCacheById(PROJECTS_CACHE_KEY, id, changes);
    try {
        await updateProjectD1(id, changes);
    } catch (error) {
        persistArrayCache(PROJECTS_CACHE_KEY, previousCache);
        console.error("Error updating project:", error);
        throw error;
    }
}

/**
 * Delete a single project from D1
 * @param {number} id - Project ID
 * @returns {Promise<void>}
 */
export async function saveProjectDelete(id) {
    try {
        await deleteProjectD1(id);
    } catch (error) {
        console.error("Error deleting project:", error);
        throw error;
    }
}
